'use client';
import LoadingSpinner from '@/components/ui/spinner';
import {
  fetchAuthSession,
  getCurrentUser,
  signOut,
} from '@aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import {
  useContext,
  useState,
  useEffect,
  useRef,
  createContext,
  PropsWithChildren,
  useCallback,
  useMemo,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter, usePathname } from 'next/navigation';
import TokenService from '@/lib/TokenService';
import { isPublicPath } from '@/lib/public-paths';
import { useUserProfileQuery } from '@/api/useUserProfile';
import { ProfileStatus } from '@/constants/profileStatus';

interface DecodedIdToken {
  iss?: string;
  sub?: string;
  aud?: string | string[];
  email?: string;
  email_verified?: boolean;
  "cognito:username"?: string;
  "cognito:groups"?: string[];
  auth_time?: number;
  exp?: number;
  iat?: number;
  [key: string]: unknown;
}

interface GetUserFullNameParams {
  shortLast?: boolean;
}

interface UserData {
  sub: string;
  email: string;
  given_name: string;
  family_name: string;
  picture?: string;
  role?:number;
  [key: string]: unknown;
}

const CTX = createContext<{
  user: UserData | null;
  idTokenPayload: DecodedIdToken | null;
  getUserFullName: (p?: GetUserFullNameParams) => string;
  getAvatarFallback: () => string;
  handleLogout: () => any;
  getUserTokens: () => Promise<{
    accessToken: string;
    idToken: string;
  }>;
  getAPIRequestTokenHeaders: () => {
    authorization: string;
    'x-id-token': string;
  };
  refreshUserAttributes: () => Promise<any>;
  tokens: {
    idToken: string;
    accessToken: string;
  };
  loading: boolean;
  // True when the signed-in user is temporarily suspended (read-only).
  isReadOnly: boolean;
  // True once the access check (profile fetch) has settled.
  isAccessResolved: boolean;
  // True when the signed-in user is permanently banned.
  isPermanentlyBanned: boolean;
}>({
  user: null,
  idTokenPayload: null,
  isReadOnly: false,
  isAccessResolved: false,
  isPermanentlyBanned: false,
} as any);

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface UserContextProps extends PropsWithChildren { }

const UserContextProvider = ({ children }: UserContextProps) => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const pathname = usePathname();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<UserData | null>(null);
  const [tokens, setTokens] = useState({ accessToken: '', idToken: '' });
  const [idTokenPayload, setIdTokenPayload] = useState<DecodedIdToken | null>(null);
  const [isIdTokenPayloadLoading, setIsIdTokenPayloadLoading] = useState(true);
  const [isBanned, setIsBanned] = useState(false);

  const decodeJwtPayload = (jwt: string): DecodedIdToken | null => {
    try {
      const token = jwt.startsWith('Bearer ') ? jwt.slice(7) : jwt;
      const parts = token.split('.');
      if (parts.length < 2) return null;
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const decoded = typeof window !== 'undefined'
        ? atob(payload)
        : Buffer.from(payload, 'base64').toString('binary');
      const json = decodeURIComponent(
        decoded
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(json) as DecodedIdToken;
    } catch {
      return null;
    }
  };

  const roleIsBusiness = useMemo(() => {
    return (idTokenPayload?.['custom:role'] === '1') === true;
  }, [idTokenPayload]);

  const {
    data: profileData,
    isLoading: isProfileLoading,
    refetch: refetchProfile,
    error: profileError,
    isError: isProfileError,
    isSuccess: isProfileSuccess,
    // Pass our own idTokenPayload — inside the provider useAuth() returns the
    // default context, so without this the query would be permanently disabled
    // and the access gate would deadlock (spinner forever, no request).
  } = useUserProfileQuery({ idTokenPayload });

  // The profile query has settled (so we KNOW whether access is allowed) once it
  // has either succeeded or errored. While it's still pending we must not let
  // any "authenticated → go to the app" redirect fire, or a banned user flashes
  // a protected page before we can bounce them.
  const isAccessResolved = isProfileSuccess || isProfileError;

  const profileStatusValue = (
    profileData?.responseObject as { status?: number } | null | undefined
  )?.status;

  // Temp suspension (ProfileStatus.Suspended): reads succeed, so the profile
  // loads and we can put the app in read-only mode proactively.
  const isReadOnly = profileStatusValue === ProfileStatus.Suspended;

  // Permanent suspension, signalled either by a 403 on the boot-time profile
  // GET (authenticateToken blocks the user) or by a 200 profile carrying
  // status === PermanentlySuspended. Derived synchronously (not via state) so
  // the access gate closes in the SAME render the signal arrives — no
  // one-frame flash of protected content before the redirect.
  const isPermanentlyBanned =
    (isProfileError &&
      (profileError as { response?: { status?: number } } | null)?.response
        ?.status === 403) ||
    profileStatusValue === ProfileStatus.PermanentlySuspended;

  // Build the user as soon as the JWT has decoded, so consumers reading
  // `user.sub`, `user.role`, etc. don't have to wait for the profile query.
  // When the profile arrives we merge its fields in. JWT-derived fields
  // (sub, email, role) are written *after* the profile spread so they always
  // win — they're the authoritative source. This also handles the no-profile
  // case (business-role users whose `/business-profiles/my` 404s): `user` is
  // still populated from the token instead of staying null forever.
  useEffect(() => {
    if (!idTokenPayload) return;
    const sub = idTokenPayload.sub || '';
    const email = idTokenPayload.email || '';
    const role = idTokenPayload['custom:role'] || undefined;
    const profile = profileData?.responseObject;
    setUser({
      ...(profile ?? {}),
      sub,
      email,
      given_name: profile?.firstName || '',
      family_name: profile?.lastName || '',
      picture: profile?.profilePictureURL || undefined,
      role,
    } as UserData);
  }, [profileData, idTokenPayload]);

  const refreshUserAttributes = async () => {
    await refetchProfile();
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await getCurrentUser();
        const { tokens: authTokens } = await fetchAuthSession();
        if (!authTokens?.accessToken || !authTokens?.idToken) {
          setLoading(false);
          setIsIdTokenPayloadLoading(false);
          return;
        }
        const accessTokenStr = authTokens.accessToken.toString();
        const idTokenStr = authTokens.idToken.toString();
        setTokens({
          accessToken: `Bearer ${accessTokenStr}`,
          idToken: `Bearer ${idTokenStr}`,
        });
        // Sync with TokenService for consistent token access across the app
        TokenService.accessToken = accessTokenStr;
        TokenService.idToken = idTokenStr;
        const decoded = decodeJwtPayload(`Bearer ${authTokens.idToken.toString()}`);
        if (decoded) {
          setIdTokenPayload(decoded);
        } else {
          setIdTokenPayload(null);
        }
        setLoading(false);
        setIsIdTokenPayloadLoading(false);
      } catch {
        // Expected when user is not authenticated (e.g., on login page)
        setLoading(false);
        setIsIdTokenPayloadLoading(false);
      }
    };
    initializeAuth();

    // Listen for auth events (e.g., OAuth redirect completion)
    const hubListener = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          // Regular sign-in completed (email/password), re-initialize auth
          initializeAuth();
          break;
        case 'signInWithRedirect':
          // OAuth redirect completed successfully, re-initialize auth
          initializeAuth();
          break;
        case 'signInWithRedirect_failure':
          console.error('OAuth sign-in failed:', payload.data);
          setLoading(false);
          setIsIdTokenPayloadLoading(false);
          break;
        case 'tokenRefresh':
          // Token was refreshed, update our state
          initializeAuth();
          break;
        case 'signedOut':
          // User signed out
          setUser(null);
          setTokens({ accessToken: '', idToken: '' });
          setIdTokenPayload(null);
          break;
      }
    });

    return () => {
      hubListener();
    };
  }, []);

  const getUserTokens = async (): Promise<{
    accessToken: string;
    idToken: string;
  }> => {
    try {
      const { tokens } = await fetchAuthSession();

      return {
        accessToken: `Bearer ${tokens?.accessToken.toString()}`,
        idToken: `Bearer ${tokens?.idToken?.toString()}`,
      };
    } catch {
      return {
        accessToken: '',
        idToken: '',
      };
    }
  };

  const getUserFullName = useCallback(
    (options?: GetUserFullNameParams) => {
      if (!user) {
        return '';
      }
      if (roleIsBusiness) {
        return (user.name as string) || '';
      }
      const firstName = user.given_name || '';
      const lastName = user.family_name || '';
      if (options?.shortLast) {
        return `${firstName} ${lastName?.[0]}.`;
      } else {
        return `${firstName} ${lastName}`;
      }
    },
    [user, roleIsBusiness]
  );

  const getAvatarFallback = () =>
    user ? (user?.given_name?.[0] ?? '') + (user?.family_name?.[0] ?? '') : '';

  // localStorage keys that must survive logout because they track a "seen
  // this once, ever" state for the account (e.g. one-time prompts) rather
  // than session data. A blanket localStorage.clear() on every logout would
  // otherwise make these prompts reappear on every subsequent login.
  const PERSISTENT_LOCAL_STORAGE_PREFIXES = [
    'pp_profile_tags_prompt_seen_v1_',
    'pp_profile_completion_seen_v2_',
  ];

  const clearAllBrowserStorage = () => {
    if (typeof window === 'undefined') return;

    // Snapshot the keys that must survive the clear below.
    const preserved: Record<string, string> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        PERSISTENT_LOCAL_STORAGE_PREFIXES.some((prefix) =>
          key.startsWith(prefix)
        )
      ) {
        const value = localStorage.getItem(key);
        if (value !== null) preserved[key] = value;
      }
    }

    // Clear localStorage
    localStorage.clear();

    // Restore the "seen once, ever" flags wiped above.
    Object.entries(preserved).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });

    // Clear sessionStorage
    sessionStorage.clear();

    // Clear all cookies
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      // Clear cookie for current domain
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      // Clear cookie for parent domain (if applicable)
      const domain = window.location.hostname;
      const domainParts = domain.split('.');
      if (domainParts.length > 1) {
        const parentDomain = '.' + domainParts.slice(-2).join('.');
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${parentDomain}`;
      }
    }

    // Clear TokenService static state
    TokenService.clear();
  };

  const handleLogout = () => {
    const cognitoDomain =  process.env.NEXT_PUBLIC_COGNITO_DOMAIN;
    const clientId = process.env.NEXT_PUBLIC_USER_POOL_CLIENT_ID;
    const redirectUri = process.env.NEXT_PUBLIC_REDIRECT_SIGN_OUT_URL!;
    const logoutUrl =
      `https://${cognitoDomain}/logout` +
      `?client_id=${clientId}` +
      `&logout_uri=${encodeURIComponent(redirectUri)}`;
      
    signOut({ global: true }).then(() => {
      clearAllBrowserStorage();
      queryClient.clear();
      setUser(null);
      window.location.href = logoutUrl;
    });
  };

  // Permanent suspension (ProfileStatus.PermanentlySuspended): the backend
  // rejects EVERY authenticated request with 403 — including the boot-time
  // `/profiles/my` GET. Because temp-suspended users CAN read, a 403 on the
  // profile fetch unambiguously means the account is permanently banned. This
  // is what actually enforces the ban for a Cognito client: signing in with
  // Cognito succeeds, but we immediately sign back out and bounce to login.
  const bannedHandledRef = useRef(false);
  const handlePermanentSuspension = async () => {
    if (bannedHandledRef.current) return;
    bannedHandledRef.current = true;
    setIsBanned(true); // gate the app immediately while we tear down + redirect
    try {
      await signOut({ global: true });
    } catch {
      // ignore — we're tearing the session down regardless
    }
    clearAllBrowserStorage();
    queryClient.clear();
    setUser(null);
    // Client-side nav (not window.location) so the login page doesn't do a
    // jarring full reload — and so this handler's once-guard isn't reset by a
    // reload, which could otherwise loop.
    router.replace('/login?suspended=permanent');
  };

  useEffect(() => {
    if (isPermanentlyBanned) {
      handlePermanentSuspension();
    }
    // handlePermanentSuspension is guarded by a ref; only react to the flag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPermanentlyBanned]);

  const getAPIRequestTokenHeaders = () => {
    // TokenService first: it's the cache the Axios interceptor and the
    // refresh timer keep fresh. The React state is only a fallback, since it
    // lags a refresh until the Hub event re-runs initializeAuth.
    const accessToken = TokenService.accessToken
      ? `Bearer ${TokenService.accessToken}`
      : tokens.accessToken;
    const idToken = TokenService.idToken
      ? `Bearer ${TokenService.idToken}`
      : tokens.idToken;
    return {
      authorization: accessToken,
      'x-id-token': idToken,
    };
  };

  // Most API calls read the token synchronously (above), so it must already
  // be fresh when they do. Refresh it a minute before expiry; and because
  // timers don't run while a laptop sleeps, check again whenever the tab
  // comes back to the foreground. A successful refresh emits Amplify's
  // `tokenRefresh` Hub event, which re-runs initializeAuth and updates the
  // React state too.
  useEffect(() => {
    if (!tokens.accessToken) return;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        try {
          await TokenService.refreshTokens();
        } catch {
          // Offline or signed out; the next request's 401 path handles it.
        }
        schedule();
      }, Math.max(TokenService.msUntilRefresh(), 5_000));
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible' && TokenService.isStale()) {
        TokenService.refreshTokens().catch(() => {});
      }
    };

    schedule();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [tokens.accessToken]);

  const isLoading = isIdTokenPayloadLoading || isProfileLoading || loading;

  // Don't render app content for an authenticated user until we've confirmed
  // they aren't permanently suspended. While the boot-time profile fetch is in
  // flight (or once a ban is detected) we hold on a spinner instead of flashing
  // a protected page and then bouncing them. Unauthenticated routes (no
  // idTokenPayload, e.g. the login page) render normally.
  const verifyingAccess =
    !!idTokenPayload &&
    (!isAccessResolved || isPermanentlyBanned || isBanned);

  // The initial token check only resolves in a client effect, so on the server
  // isIdTokenPayloadLoading is always true. Gating on it unconditionally meant
  // every route — including fully public ones — server-rendered a spinner
  // instead of its content, leaving crawlers an empty page with no links.
  // Public routes have nothing to protect, so they skip that gate and render
  // immediately. Ban/access checks below still apply everywhere.
  const onPublicRoute = isPublicPath(pathname ?? '/');

  return (
    <CTX.Provider
      value={{
        user,
        idTokenPayload,
        getUserFullName,
        getAvatarFallback,
        handleLogout,
        getUserTokens,
        tokens,
        loading: isLoading,
        refreshUserAttributes,
        getAPIRequestTokenHeaders,
        isReadOnly,
        isAccessResolved,
        isPermanentlyBanned,
      }}
    >
      {(!onPublicRoute && isIdTokenPayloadLoading) || verifyingAccess ? (
        <LoadingSpinner />
      ) : (
        children
      )}
    </CTX.Provider>
  );
};

export default UserContextProvider;

export const useAuth = () => {
  return useContext(CTX);
};
